<template>
   <div class="card">
      <div class="card-body m-2">
         <div class="row p-2 align-items-center">

            <!-- Search -->
            <div class="col-12 col-lg-4 mb-2 mb-lg-0 d-flex gap-2">
               <input v-model="search" type="text" class="form-control form-control-sm"
                  placeholder="&#xF002; Search active keys..."
                  style="border-radius:8px; height: 40px; font-size: 14px; font-family: 'Roboto', sans-serif, 'Font Awesome 6 Free'; font-weight: 900;"
                  @keyup="onSearch()" />
            </div>

            <!-- Filters + Pagination -->
            <div class="col-12 col-lg d-flex flex-wrap justify-content-lg-end align-items-center">

               <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                  <span class="p-2 filterTitle">Sort by:</span>
                  <select v-model="sortBy" class="form-select shadow-none filterOptions bordered-select">
                     <option v-for="s in sortByList" :value="s.value" :key="s.value">{{ s.text }}</option>
                  </select>
               </div>

               <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                  <span class="p-2 filterTitle">Rows per page:</span>
                  <select v-model="pagination.pageSize"
                     class="form-select form-select-sm shadow-none filterOptions bordered-select" style="width: auto;">
                     <option v-for="row in rowsPerPageList" :value="row.value" :key="row.value">{{ row.text }}</option>
                  </select>
               </div>

               <div class="pagination" v-if="pagination.totalItems > 0">
                  <button @click="previousPage()" :disabled="pagination.page == 1" class="pagination-arrow">
                     <i class="fa fa-chevron-left"></i>
                  </button>
                  <span class="pagination-label">
                     <strong>{{ paginationLabel }}</strong> of {{ pagination.totalItems }}
                  </span>
                  <button @click="nextPage()"
                     :disabled="pagination.page == pagination.totalPages || pagination.totalItems == 0"
                     class="pagination-arrow">
                     <i class="fa fa-chevron-right"></i>
                  </button>
               </div>

            </div>
         </div>

         <!-- Table -->
         <div class="row p-2">
            <table class="table align-middle mainTable">
               <thead class="align-middle">
                  <tr>
                     <th scope="col" v-for="header in headers" :key="header.value">{{ header.text }}</th>
                  </tr>
               </thead>

               <tbody v-if="active_keys.length <= 0">
                  <tr>
                     <td :colspan="headers.length">No records to display.</td>
                  </tr>
               </tbody>

               <tbody v-else>
                  <tr v-for="(key, index) in active_keys" :key="index">
                     <td>{{ key.name || '—' }}</td>
                     <td style="font-family: monospace; font-size: 12px;">{{ key.api_key }}</td>
                     <td>{{ key.host_name || '—' }}</td>
                     <td>
                        <span class="days-pill" :class="getDaysClass(key.days)">
                           {{ key.days }} {{ key.days === 1 ? 'day' : 'days' }}
                        </span>
                     </td>
                  </tr>
               </tbody>
            </table>
         </div>

      </div>
   </div>
</template>

<script>
import moment from "moment";
import { debounce } from '@/plugins/helper.js'

export default {
   data() {
      return {
         headers: [
            { text: "Key Name", value: "keyName" },
            { text: "Extension Key", value: "extensionKey" },
            { text: "Hostname", value: "hostName" },
            { text: "Last Active", value: "days" },
         ],
         active_keys: [],
         search: "",
         debounceFn: null,
         paginationData: {},
         pagination: {
            pageSize: 50, page: 1, totalItems: 0, totalPages: 1,
         },
         rowsPerPageList: [
            { text: "50", value: 50 },
            { text: "75", value: 75 },
            { text: "100", value: 100 },
         ],
         sortByList: [
            { text: "Days: Ascending", value: "days__asc" },
            { text: "Days: Descending", value: "days__desc" },
            { text: "Extension Key: A-Z", value: "key__asc" },
            { text: "Extension Key: Z-A", value: "key__desc" },
         ],
         sortBy: "days__asc",
         sorting: { field: "days", order: "asc" },
      };
   },

   computed: {
      paginationLabel() {
         var start = Math.min((this.pagination.page - 1) * this.pagination.pageSize + 1, this.pagination.totalItems)
         var end = Math.min((this.pagination.page * this.pagination.pageSize), this.pagination.totalItems)
         return start + " - " + end
      },
      currentPage() { return this.pagination.page },
      rowsPerPage() { return this.pagination.pageSize },
      sortItems() { return this.sortBy },
   },

   watch: {
      currentPage() { this.getActiveKeysWithFilters() },
      rowsPerPage() { this.pagination.page = 1; this.getActiveKeysWithFilters() },
      sortItems() {
         if (this.sortBy.includes("__")) {
            let sort = this.sortBy.split("__")
            this.sorting.field = sort[0]
            this.sorting.order = sort[1]
         } else {
            this.sorting.field = this.sortBy
            this.sorting.order = ""
         }
         this.getActiveKeysWithFilters()
      },
   },

   created() {
      this.debounceFn = debounce(() => this.getActiveKeysWithFilters(), 500)
      this.getActiveKeysWithFilters()
   },

   methods: {
      getDaysClass(days) {
         if (days <= 1) return 'days-recent';
         if (days <= 7) return 'days-normal';
         return 'days-old';
      },

      formatDate(date) { return moment(date).format("DD-MMM-YYYY") },

      previousPage() { this.pagination.page > 1 ? this.pagination.page -= 1 : '' },
      nextPage() { this.pagination.totalPages > this.pagination.page ? this.pagination.page += 1 : '' },
      onSearch() { this.pagination.page = 1; this.debounceFn() },

      async getActiveKeysWithFilters() {
         let apiPath = "analytics/" + this.$store.state.auth.organisation_uuid + "/get-account-active-keys?page_size=" + this.pagination.pageSize + "&page=" + this.pagination.page

         if (this.sorting.order != "" && this.sorting.field != "") {
            apiPath += "&order_by_field=" + this.sorting.field
            apiPath += "&order_by=" + this.sorting.order
         }
         if (this.search !== "" || this.search !== null) {
            apiPath += "&search=" + this.search
         }

         await this.$axios.get(apiPath)
            .then((response) => {
               if (response.data.active_keys === "No Record Found")
                  this.active_keys = []
               else {
                  this.active_keys = response.data.active_keys
               }
               this.paginationData = response.data.pagination
               this.pagination.totalItems = this.paginationData.total_count
               this.pagination.totalPages = Math.ceil(this.paginationData.total_count / this.pagination.pageSize)
            })
            .catch((error) => {
               this.$toast.error(error.response.data.message)
            })
      },
   }
}
</script>

<style scoped>
.card { border: none; }

.days-pill {
   padding: 4px 10px;
   border-radius: 6px;
   font-size: 11px;
   font-weight: 600;
   display: inline-flex;
   align-items: center;
   white-space: nowrap;
}
.days-recent { background: #e6f9ee; color: #00873c; }
.days-normal { background: #e7f6f9; color: #00809a; }
.days-old { background: #fff8e1; color: #e6a100; }
</style>